import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useMemo, useState } from 'react'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface NeedsSortingClient {
    manual_claim_id: number
    case_ref: string
    client_id: number
    status: string
    lender: string | null
    policy_number: string | null
    vehicle_make: string | null
    vehicle_model: string | null
    vehicle_registration: string | null
    apr: string | null
    credit_amount: string | null
    finance_amount: string | null
    finance_agreement_url: string | null
    send_email: number
    send_sms: number
    created_at: string | null
    title: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
    phone: string | null
    postcode: string | null
    reused_client: number
    signature_token: string | null
    signature_status: string | null
    signed: number | null
    signature_date: string | null
    link_send_date: string | null
}

const CFA_PORTAL_BASE = 'https://portal.sentinellegal.co.uk/cfa-litigation'

const fullName = (client: NeedsSortingClient): string =>
    [client.title, client.first_name, client.last_name].filter(Boolean).join(' ').trim() || '—'

const formatDate = (value: string | null): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const isSigned = (client: NeedsSortingClient): boolean =>
    client.signed === 1 || String(client.signature_status || '').toLowerCase().includes('signed')

// Unapproved-lender clients have no claim we can pursue, so no CFA is generated
// and their CFA status is N/A.
const isNoClaim = (client: NeedsSortingClient): boolean =>
    String(client.status || '').toLowerCase().includes('no claim')

export const NeedsSortingClientsView = () => {
    const [clients, setClients] = useState<NeedsSortingClient[]>([])
    const [loaded, setLoaded] = useState(false)
    const [filter, setFilter] = useState('')

    const fetchClients = async () => {
        Loading.circle('Loading needs sorting clients...')
        try {
            const response = await api.post('manual-needs-sorting-clients', {})
            if (!response.data?.success) {
                Report.failure('Error', response.data?.message || 'Could not load needs sorting clients.', 'Okay')
                return
            }
            setClients(Array.isArray(response.data.clients) ? response.data.clients : [])
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Could not load needs sorting clients.'
            Report.failure('Error', message, 'Okay')
        } finally {
            Loading.remove()
            setLoaded(true)
        }
    }

    useEffect(() => {
        fetchClients()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filteredClients = useMemo(() => {
        const term = filter.trim().toLowerCase()
        if (!term) return clients
        return clients.filter((client) => {
            const haystack = [
                fullName(client),
                client.email,
                client.phone,
                client.case_ref,
                client.lender,
                client.policy_number,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(term)
        })
    }, [clients, filter])

    const signedCount = useMemo(() => clients.filter(isSigned).length, [clients])
    const noClaimCount = useMemo(() => clients.filter(isNoClaim).length, [clients])
    const awaitingCount = clients.length - signedCount - noClaimCount

    return (
        <div className={styles.listView}>
            <h3>
                <span>Clients awaiting sorting</span>
                <button type="button" className={styles.refreshButton} onClick={fetchClients}>
                    Refresh
                </button>
            </h3>

            <p className={styles.intro}>
                Manually-added clients flagged <strong>Needs Sorting</strong>, including whether they have signed their
                CFA. No-claim clients (unapproved lender) are listed too — they have no case to pursue, so no CFA is
                generated and their CFA status is N/A. This is a read-only overview.
            </p>

            <div className={styles.summaryRow}>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{clients.length}</span>
                    <span className={styles.summaryLabel}>Total needs sorting</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{signedCount}</span>
                    <span className={styles.summaryLabel}>CFA signed</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{awaitingCount}</span>
                    <span className={styles.summaryLabel}>Awaiting signature</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{noClaimCount}</span>
                    <span className={styles.summaryLabel}>No claim (unapproved lender)</span>
                </div>
            </div>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Filter by name, email, case ref or lender..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <span className={styles.resultCount}>
                    {filteredClients.length} of {clients.length} shown
                </span>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Contact</th>
                            <th>Case Ref</th>
                            <th>Lender</th>
                            <th>Finance Agreement</th>
                            <th>CFA Status</th>
                            <th>State</th>
                            <th>Added</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClients.map((client) => {
                            const signed = isSigned(client)
                            return (
                                <tr key={client.manual_claim_id}>
                                    <td>
                                        <div className={styles.clientCell}>
                                            <strong>{fullName(client)}</strong>
                                            <span
                                                className={
                                                    client.reused_client === 1 ? styles.tagExisting : styles.tagNew
                                                }
                                            >
                                                {client.reused_client === 1 ? 'Existing client' : 'New client'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.contactCell}>
                                            <span>{client.email || '—'}</span>
                                            <span className={styles.muted}>{client.phone || '—'}</span>
                                        </div>
                                    </td>
                                    <td className={styles.mono}>{client.case_ref || '—'}</td>
                                    <td>{client.lender || '—'}</td>
                                    <td>
                                        {client.finance_agreement_url ? (
                                            <a
                                                href={client.finance_agreement_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.docLink}
                                            >
                                                View document
                                            </a>
                                        ) : (
                                            <span className={styles.muted}>Not provided</span>
                                        )}
                                    </td>
                                    <td>
                                        {isNoClaim(client) ? (
                                            <div className={styles.cfaCell}>
                                                <span className={styles.badgeNa}>N/A</span>
                                                <span className={styles.muted}>No claim — unapproved lender</span>
                                            </div>
                                        ) : client.signature_token ? (
                                            <div className={styles.cfaCell}>
                                                <span className={signed ? styles.badgeSigned : styles.badgeUnsigned}>
                                                    {signed ? 'Signed' : 'Awaiting signature'}
                                                </span>
                                                {signed && client.signature_date && (
                                                    <span className={styles.muted}>
                                                        {formatDate(client.signature_date)}
                                                    </span>
                                                )}
                                                {!signed && (
                                                    <a
                                                        href={`${CFA_PORTAL_BASE}/${client.signature_token}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.docLink}
                                                    >
                                                        Signing link
                                                    </a>
                                                )}
                                            </div>
                                        ) : (
                                            <span className={styles.muted}>No link</span>
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={
                                                isNoClaim(client) ? styles.statusBadgeNoClaim : styles.statusBadge
                                            }
                                        >
                                            {client.status || 'Needs Sorting'}
                                        </span>
                                    </td>
                                    <td className={styles.muted}>{formatDate(client.created_at)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>

                {loaded && filteredClients.length === 0 && (
                    <div className={styles.emptyState}>
                        {clients.length === 0
                            ? 'No clients are currently flagged as needs sorting.'
                            : 'No clients match your filter.'}
                    </div>
                )}
            </div>
        </div>
    )
}
